import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { parseQueryString } from '../common/querystring-filters'
import withConfig from '../van-shared/hoc/withConfig'
import { withAuth } from '../common/AuthenticationProvider'
import { hasFeature } from '../van-shared/utils/features'

import Login from './Login'

class LoginContainer extends Component {
  static propTypes = {
    resetLoginStatus: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    login: PropTypes.func.isRequired,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    config: PropTypes.object.isRequired,
    loginInProgress: PropTypes.bool
  }

  static defaultProps = {
    error: null,
    loginInProgress: false
  }

  componentDidMount () {
    this.props.resetLoginStatus()
  }

  /**
   * Handles the login submission process.
   *
   * @function handleLoginSubmit
   * @param {Object} fields - The login fields.
   * @param {string} fields.username - The username entered by the user.
   * @param {string} fields.password - The password entered by the user.
   * @param {string} fields.loginAsUser - The optional username for a user logging in as a different user.
   * @returns {Promise | undefined} The result of the login attempt.
   *
   * @description
   * This function handles the login submission by validating the provided username and password.
   * It also determines if the login attempt is for an admin user, which lets the user log in with a different user account.
   * URL to accomplish this admin login could be something like e.g. https://shelflife.field.supply/login?loginAs=admin
   */
  handleLoginSubmit = (fields) => {
    const qsProps = parseQueryString(this.props.location.search)

    if (fields.username && fields.password) {
      const { location, history, config } = this.props
      const { from } = location.state || { from: { pathname: '/' } }

      return this.props.login(
        fields.username,
        fields.password,
        {
          isAdminLogin: qsProps.loginAs && qsProps.loginAs === 'admin',
          loggedInUserName: fields.loginAsUser,
          history,
          from,
          config
        }
      )
    }
  }

  render () {
    const {
      error,
      config
    } = this.props

    const qsProps = parseQueryString(this.props.location.search)

    let errorMessage = null

    if (error) {
      const fallbackErrMsg = 'Error logging in: Please make sure that your username and password are correct and try again. If the problem persists, please contact support.'

      if (error.reason) {
        errorMessage = hasFeature(config.features, 'allowResetPass')
          ? (
            <div>
              Error logging in: Incorrect username or password. If you've forgotten your password, please use the <a href='/forgot-password' className='vs-u-link vs-u-link--inline'>Forgot your password</a> link to reset your password.
            </div>
          )
          : fallbackErrMsg
      } else if (error.message) {
        errorMessage = error.message
      } else {
        errorMessage = fallbackErrMsg
      }
    }

    return (
      <Login
        config={this.props.config}
        onSubmit={this.handleLoginSubmit}
        loginInProgress={this.props.loginInProgress}
        errorMessage={errorMessage}
        extras={qsProps}
      />
    )
  }
}

export default withAuth('login', 'loginInProgress', 'error', 'resetLoginStatus')(withConfig(LoginContainer))
